import { t } from "@lingui/core/macro";
import { NavigationProp, useNavigation } from "@react-navigation/native";
import graphql from "babel-plugin-relay/macro";
import { useFragment } from "react-relay";

import { Button } from "@whoosh/design-system/src/components/controls";
import { ChevronRightIcon } from "@whoosh/design-system/src/components/icons_v2/general";
import {
  HStack,
  Text,
  VStack,
} from "@whoosh/design-system/src/components/primitive";

import { StoreProductList } from "../../StoreProductList";
import { StoreStackNavigatorParamList } from "../../StoreStackNavigator";
import { StoreHomeDesktop_session$key } from "./__generated__/StoreHomeDesktop_session.graphql";

type Props = {
  sessionRef?: StoreHomeDesktop_session$key | null;
  onPackageAuthComplete?: () => void;
};

export const StoreHomeDesktop = ({
  sessionRef,
  onPackageAuthComplete,
}: Props) => {
  const navigation =
    useNavigation<NavigationProp<StoreStackNavigatorParamList>>();

  const goToMemberships = () => {
    navigation.navigate("Memberships");
  };

  const goToPackages = () => {
    navigation.navigate("Packages");
  };

  const session = useFragment(StoreHomeDesktopFragmentString, sessionRef);
  const club = session?.currentClientProfile?.member?.club;

  const memberships = club?.memberships;
  const hasMemberships = (memberships?.count ?? 0) > 0;

  const packages = club?.packages;
  const hasPackages = (packages?.count ?? 0) > 0;

  return (
    <VStack pt={8} gap={8}>
      {hasMemberships ? (
        <VStack>
          <HStack alignItems="center" justifyContent="space-between">
            <Text variant="mainHeading">{t`Memberships`}</Text>

            <Button
              accessibilityLabel={t`See all memberships`}
              size="small"
              variant="white"
              rightIcon={<ChevronRightIcon />}
              onPress={goToMemberships}
            >
              {t`See all memberships`}
            </Button>
          </HStack>
          <StoreProductList
            sessionRef={session}
            packageConnectionRef={memberships}
            onPackageAuthComplete={onPackageAuthComplete}
          />
        </VStack>
      ) : null}
      {hasPackages ? (
        <VStack>
          <HStack alignItems="center" justifyContent="space-between">
            <Text variant="mainHeading">{t`Packages`}</Text>

            <Button
              accessibilityLabel={t`See all packages`}
              size="small"
              variant="white"
              rightIcon={<ChevronRightIcon />}
              onPress={goToPackages}
            >{t`See all packages`}</Button>
          </HStack>
          <StoreProductList
            sessionRef={session}
            packageConnectionRef={packages}
            onPackageAuthComplete={onPackageAuthComplete}
          />
        </VStack>
      ) : null}
    </VStack>
  );
};

const StoreHomeDesktopFragmentString = graphql`
  fragment StoreHomeDesktop_session on Session {
    ...StoreProductList_session
    currentClientProfile {
      member {
        club {
          packages(
            packageType: STANDARD
            first: 4
            assignable: true
            saleAvailability: PUBLIC
          ) {
            count
            ...StoreProductList_packageConnection
          }
          memberships: packages(
            packageType: MEMBERSHIP
            first: 4
            assignable: true
            saleAvailability: PUBLIC
          ) {
            count
            ...StoreProductList_packageConnection
          }
        }
      }
    }
  }
`;
